import { parseCadMoneyInputToCents } from '../../utils/publicCadMoney.js';

export const PUBLIC_RESPONSE_SCHEMA_VERSION_V2 = 2;
export const PUBLIC_RESPONSE_SCHEMA_VERSION_V3 = 3;

export const VALUATION_UNIT_MIX_KEYS = Object.freeze([
  'oneAndHalf',
  'twoAndHalf',
  'threeAndHalf',
  'fourAndHalf',
  'fiveAndHalf',
  'sixAndHalfPlus',
  'commercial',
  'other',
]);

export const VALUATION_EXPENSE_KEYS = Object.freeze([
  'municipalTaxes',
  'schoolTaxes',
  'insurance',
  'heatingElectricity',
]);

export const CONSTRUCTION_YEAR_MIN = 1800;

export function createEmptyUnitMix() {
  return {
    oneAndHalf: 0,
    twoAndHalf: 0,
    threeAndHalf: 0,
    fourAndHalf: 0,
    fiveAndHalf: 0,
    sixAndHalfPlus: 0,
    commercial: 0,
    other: 0,
  };
}

export function createEmptyValuationDraft() {
  return {
    grossAnnualIncomeText: '',
    municipalTaxesText: '',
    municipalTaxesUnknown: false,
    schoolTaxesText: '',
    schoolTaxesUnknown: false,
    insuranceText: '',
    insuranceUnknown: false,
    heatingElectricityText: '',
    heatingElectricityUnknown: false,
    otherExpensesText: '',
    otherExpensesDescription: '',
    constructionYearText: '',
    constructionYearUnknown: false,
    /** True after the first imported-year init attempt for this draft (even when hint was absent). */
    constructionYearHintInitialized: false,
    unitMix: createEmptyUnitMix(),
    unitCountMismatchConfirmed: false,
  };
}

/**
 * Project a usable imported construction year for draft prefill.
 * Missing / zero / invalid / unsupported → null (no inference).
 */
export function projectUsableImportedConstructionYear(raw, { nowYear = new Date().getFullYear() } = {}) {
  if (!Number.isInteger(raw) || raw <= 0) return null;
  if (!Number.isInteger(nowYear)) return null;
  if (raw < CONSTRUCTION_YEAR_MIN || raw > nowYear) return null;
  return raw;
}

/**
 * Apply the imported construction-year hint only once per fresh draft.
 * Resolver refresh, language switch, step navigation, and retries must not
 * overwrite a manual year, a deliberately cleared year, or “I don’t know”.
 * Checking emptiness alone is insufficient — initialization is tracked explicitly.
 */
export function applyImportedConstructionYearHint(valuation, hintYear, { nowYear = new Date().getFullYear() } = {}) {
  const current = valuation && typeof valuation === 'object' ? valuation : createEmptyValuationDraft();
  if (current.constructionYearHintInitialized === true) {
    return current;
  }
  const year = projectUsableImportedConstructionYear(hintYear, { nowYear });
  if (year == null) {
    return {
      ...current,
      constructionYearHintInitialized: true,
    };
  }
  return {
    ...current,
    constructionYearText: String(year),
    constructionYearUnknown: false,
    constructionYearHintInitialized: true,
  };
}

/**
 * Fresh valuation draft used when the owner selects an intent (including property_value).
 * Initializes the imported-year hint from the already-validated public property year and
 * always marks initialization complete — even when no usable year exists — so a later
 * resolver refresh cannot treat an empty field as permission to re-apply.
 */
export function createFreshValuationDraftWithImportedYearHint(
  importedConstructionYear,
  { nowYear = new Date().getFullYear() } = {},
) {
  return applyImportedConstructionYearHint(createEmptyValuationDraft(), importedConstructionYear, {
    nowYear,
  });
}

/**
 * Owner edits to the construction year (including deliberate clears) claim the draft
 * so a subsequent access refresh cannot initialize over them.
 */
export function applyConstructionYearTextEdit(valuation, text) {
  return {
    ...valuation,
    constructionYearText: typeof text === 'string' ? text : '',
    constructionYearHintInitialized: true,
  };
}

/**
 * Checking “I don’t know” clears any previously entered year so it cannot
 * remain as a hidden value that fails XOR validation.
 * Also marks the draft initialized so refreshes cannot re-apply a hint.
 */
export function applyConstructionYearUnknown(valuation, unknown) {
  if (unknown === true) {
    return {
      ...valuation,
      constructionYearUnknown: true,
      constructionYearText: '',
      constructionYearHintInitialized: true,
    };
  }
  return {
    ...valuation,
    constructionYearUnknown: false,
    constructionYearHintInitialized: true,
  };
}

/**
 * Ordered public-page transitions for construction-year prefill.
 * Mirrors PublicMailingLandingPage: access resolve, intent selection, edits, refresh.
 * Used by the page and by lifecycle tests — do not reimplement this sequence in tests only.
 *
 * @param {{ property: object|null, valuation: object, valuationStep: number, selectedIntent: string|null }} state
 * @param {{ type: string, [key: string]: unknown }} action
 */
export function reducePublicValuationConstructionYearLifecycle(state, action) {
  const current = state && typeof state === 'object'
    ? state
    : { property: null, valuation: createEmptyValuationDraft(), valuationStep: 1, selectedIntent: null };
  const valuation = current.valuation || createEmptyValuationDraft();

  switch (action?.type) {
    case 'resolve_access': {
      const property = action.property && typeof action.property === 'object' ? action.property : null;
      return {
        ...current,
        property,
        valuation: applyImportedConstructionYearHint(valuation, property?.constructionYear),
      };
    }
    case 'select_intent': {
      // Intent selection always starts a new valuation draft (sale/guide wipe prior edits).
      // Prefill must happen here — not only on the earlier resolve — or property_value loses the year.
      return {
        ...current,
        selectedIntent: action.intent,
        valuationStep: 1,
        valuation: createFreshValuationDraftWithImportedYearHint(current.property?.constructionYear),
      };
    }
    case 'edit_construction_year':
      return {
        ...current,
        valuation: applyConstructionYearTextEdit(valuation, action.text),
      };
    case 'set_construction_year_unknown':
      return {
        ...current,
        valuation: applyConstructionYearUnknown(valuation, action.unknown === true),
      };
    case 'resolver_refresh': {
      // Same as resolve_access against the current property — must not overwrite an initialized draft.
      return {
        ...current,
        valuation: applyImportedConstructionYearHint(
          valuation,
          current.property?.constructionYear ?? action.constructionYear,
        ),
      };
    }
    case 'set_valuation_step':
      return {
        ...current,
        valuationStep: action.step,
        valuation,
      };
    case 'finish_success':
      // Intentional wipe after submission; next select_intent may initialize again.
      return {
        ...current,
        selectedIntent: null,
        valuationStep: 1,
        valuation: createEmptyValuationDraft(),
        property: action.property && typeof action.property === 'object' ? action.property : current.property,
      };
    case 'token_reset':
      return {
        property: null,
        selectedIntent: null,
        valuationStep: 1,
        valuation: createEmptyValuationDraft(),
      };
    default:
      return current;
  }
}

function moneyErrorReason(reason) {
  if (reason === 'empty') return 'required';
  if (reason === 'negative') return 'moneyNegative';
  if (reason === 'upperBound') return 'moneyUpperBound';
  return 'moneyInvalid';
}

/**
 * Principal / other expense drafts: blank → integer 0 cents.
 * Invalid non-empty values still fail. Unknown is handled by the caller.
 * Locale-aware — does not depend on blur/DOM normalization.
 *
 * @param {unknown} text
 * @param {'fr'|'en'} [language='fr']
 * @returns {{ ok: true, cents: number } | { ok: false, reason: string }}
 */
export function parseExpenseDraftToCents(text, language = 'fr') {
  const raw = typeof text === 'string' ? text.trim() : text == null ? '' : String(text).trim();
  if (!raw) return { ok: true, cents: 0 };
  return parseCadMoneyInputToCents(raw, language);
}

/**
 * Validate valuation step 1 (financial) draft fields.
 * Blank principal/other expenses normalize to 0 cents; gross income stays required.
 * Uses the same locale-aware path as response construction (blur-independent).
 *
 * @param {object} valuation
 * @param {{ language?: 'fr'|'en' }} [options]
 */
export function validateValuationFinancialStep(valuation, { language = 'fr' } = {}) {
  const lang = language === 'en' ? 'en' : 'fr';
  const errors = {};
  const gross = parseCadMoneyInputToCents(valuation?.grossAnnualIncomeText, lang);
  if (!gross.ok) errors.grossAnnualIncome = moneyErrorReason(gross.reason);

  for (const key of VALUATION_EXPENSE_KEYS) {
    const unknown = valuation?.[`${key}Unknown`] === true;
    if (unknown) continue;
    const parsed = parseExpenseDraftToCents(valuation?.[`${key}Text`], lang);
    if (!parsed.ok) errors[key] = moneyErrorReason(parsed.reason);
  }

  const other = parseExpenseDraftToCents(valuation?.otherExpensesText, lang);
  if (!other.ok) errors.otherExpenses = moneyErrorReason(other.reason);
  else if (other.cents > 0) {
    const desc = typeof valuation?.otherExpensesDescription === 'string'
      ? valuation.otherExpensesDescription.trim()
      : '';
    if (!desc) errors.otherExpensesDescription = 'otherExpensesDescriptionRequired';
    else if (desc.length > 500) errors.otherExpensesDescription = 'moneyInvalid';
  }

  return errors;
}

export function validateValuationBuildingStep(valuation, { importedNumberOfUnits = null, nowYear = new Date().getFullYear() } = {}) {
  const errors = {};
  const unknown = valuation?.constructionYearUnknown === true;
  const yearText = typeof valuation?.constructionYearText === 'string'
    ? valuation.constructionYearText.trim()
    : '';

  if (unknown) {
    if (yearText) errors.constructionYear = 'constructionYearXor';
  } else if (!yearText) {
    errors.constructionYear = 'constructionYearXor';
  } else if (!/^\d{4}$/.test(yearText)) {
    errors.constructionYear = 'constructionYearInvalid';
  } else {
    const year = Number(yearText);
    if (!Number.isInteger(year) || year < CONSTRUCTION_YEAR_MIN || year > nowYear) {
      errors.constructionYear = 'constructionYearInvalid';
    }
  }

  const unitMix = valuation?.unitMix || {};
  let total = 0;
  for (const key of VALUATION_UNIT_MIX_KEYS) {
    const value = unitMix[key];
    if (!Number.isInteger(value) || value < 0 || value > 9999) {
      errors[`unitMix.${key}`] = 'unitMixInvalid';
    } else {
      total += value;
    }
  }
  if (!errors['unitMix.oneAndHalf'] && total <= 0) errors.unitMix = 'unitMixRequired';

  if (
    Number.isInteger(importedNumberOfUnits) &&
    importedNumberOfUnits > 0 &&
    total > 0 &&
    total !== importedNumberOfUnits &&
    valuation?.unitCountMismatchConfirmed !== true
  ) {
    errors.unitCountMismatchConfirmed = 'unitMismatchRequired';
  }

  return errors;
}

export function deriveDeclaredUnitTotal(unitMix) {
  let total = 0;
  for (const key of VALUATION_UNIT_MIX_KEYS) {
    const value = unitMix?.[key];
    if (!Number.isInteger(value) || value < 0) return null;
    total += value;
  }
  return total;
}

/**
 * Build the schema-v3 property_value response payload from in-memory drafts.
 * Returns { response } or { errors }.
 */
export function buildPropertyValueV3Response({ form, valuation, language }) {
  const lang = language === 'en' ? 'en' : 'fr';
  const financialErrors = validateValuationFinancialStep(valuation, { language: lang });
  const buildingErrors = validateValuationBuildingStep(valuation, {
    importedNumberOfUnits:
      Number.isInteger(form?.importedNumberOfUnitsHint) && form.importedNumberOfUnitsHint > 0
        ? form.importedNumberOfUnitsHint
        : null,
  });
  const contactErrors = {};
  const fullName = typeof form?.contact?.fullName === 'string' ? form.contact.fullName.trim() : '';
  const email = typeof form?.contact?.email === 'string' ? form.contact.email.trim() : '';
  if (!fullName) contactErrors.fullName = 'required';
  if (!email) contactErrors.email = 'required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) contactErrors.email = 'emailInvalid';
  if (form?.consentAccepted !== true) contactErrors.consent = 'consentRequired';

  const errors = { ...financialErrors, ...buildingErrors, ...contactErrors };
  if (Object.keys(errors).length) return { errors };

  const gross = parseCadMoneyInputToCents(valuation.grossAnnualIncomeText, lang);
  const expenses = {};
  for (const key of VALUATION_EXPENSE_KEYS) {
    const centsKey = `${key}Cents`;
    if (valuation[`${key}Unknown`] === true) expenses[centsKey] = null;
    else expenses[centsKey] = parseExpenseDraftToCents(valuation[`${key}Text`], lang).cents;
  }
  const other = parseExpenseDraftToCents(valuation.otherExpensesText, lang);
  expenses.otherExpensesCents = other.cents;

  // Blank or explicit zero → description null; description required only when amount > 0.
  let otherExpensesDescription = null;
  if (other.cents > 0) {
    otherExpensesDescription = valuation.otherExpensesDescription.trim().replace(/\s+/g, ' ');
  }

  const constructionYearUnknown = valuation.constructionYearUnknown === true;
  const constructionYear = constructionYearUnknown
    ? null
    : Number(valuation.constructionYearText.trim());

  const unitMix = {};
  for (const key of VALUATION_UNIT_MIX_KEYS) unitMix[key] = valuation.unitMix[key];
  const declared = deriveDeclaredUnitTotal(unitMix);
  const imported =
    Number.isInteger(form?.importedNumberOfUnitsHint) && form.importedNumberOfUnitsHint > 0
      ? form.importedNumberOfUnitsHint
      : null;
  const mismatch =
    imported != null && declared !== imported ? valuation.unitCountMismatchConfirmed === true : false;

  return {
    response: {
      schemaVersion: PUBLIC_RESPONSE_SCHEMA_VERSION_V3,
      intent: 'property_value',
      contact: {
        fullName,
        email: email.toLowerCase(),
        phone: null,
      },
      language,
      consentAccepted: true,
      financialSnapshot: {
        currency: 'CAD',
        period: 'annual_current',
        grossAnnualIncomeCents: gross.cents,
        expenses,
        otherExpensesDescription,
      },
      propertyProfile: {
        constructionYear,
        constructionYearUnknown,
        unitMix,
        unitCountMismatchConfirmed: mismatch,
      },
    },
  };
}
